// Bottom-centred subtitle overlay for tutorial narration.
//
// Mounts a single fixed-position div on `document.body`. Text content is
// driven externally — the narrator engine calls setText('') to hide and
// setText('…') to show the next line. Style is fixed (dark transparent
// pill, white text, large enough to read at a glance without dominating
// the frame).

export interface SubtitleOverlay {
  setText(text: string): void;
  remove(): void;
}

export function mountSubtitleOverlay(): SubtitleOverlay {
  const wrap = document.createElement('div');
  wrap.style.cssText = [
    'position: fixed',
    'left: 50%',
    'bottom: 6vh',
    'transform: translateX(-50%)',
    'max-width: min(960px, 92vw)',
    'padding: 14px 22px',
    'background: rgba(20, 22, 26, 0.78)',
    'color: #ffffff',
    // System font stack reads cleanly without pulling in a webfont.
    'font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    'font-size: clamp(16px, 1.6vw, 22px)',
    'line-height: 1.35',
    'text-align: center',
    'border-radius: 10px',
    'box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35)',
    'opacity: 0',
    'transition: opacity 220ms ease',
    'pointer-events: none',
    'z-index: 1500',
    'white-space: pre-wrap'
  ].join('; ');
  document.body.appendChild(wrap);

  let lastText = '';
  // Pending hide-fade timer — captured so a NEW setText() can cancel it
  // before it clobbers the freshly-shown line. Without this guard, a
  // 240 ms fade-out from line A would still fire after line B was already
  // displayed and re-clear it (the bug the user reported: "end of the
  // last subtitle ends up hiding the new subtitle").
  let hideTimer: number | null = null;
  return {
    setText(text: string): void {
      if (text === lastText) return;
      lastText = text;
      // Any pending fade-out from a previous setText('') must NOT fire
      // once we've moved on — clear it whether we're showing a new line
      // or re-hiding.
      if (hideTimer !== null) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      if (text) {
        wrap.textContent = text;
        // Two rAFs ensure the textContent change is flushed before the
        // opacity transition picks up — without this the first line
        // sometimes pops in instantly.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => { wrap.style.opacity = '1'; });
        });
      } else {
        wrap.style.opacity = '0';
        // Defer clearing text until after the fade so the element doesn't
        // visually empty mid-transition.
        hideTimer = window.setTimeout(() => {
          hideTimer = null;
          if (lastText === '') wrap.textContent = '';
        }, 240);
      }
    },
    remove(): void {
      wrap.remove();
    }
  };
}
